import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MapContainer, AttributionControl, Popup } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { divIcon, latLngBounds, point } from "leaflet";
import { useNavigate, useParams } from "react-router-dom";

import CompanyMarker, { PopupContent } from "./MapPopUp";
import FlyToMarker from "./FlyToMarker";
import FitToTileCoverage from "./FitToTileCoverage";
import MapTileLayers from "./MapTileLayers";
import SaarlandOverlay from "./SaarlandOverlay";
import {useTranslation} from "react-i18next";
import {DOMAIN_URL, TILE_COVERAGE_BOUNDS, MAP_CORE_BOUNDS} from "../../enum";
import SeoManager from "../../service/SeoManager";
import strapiService from "../../strapi/StrapiService";

const COVERAGE_BOUNDS = latLngBounds(TILE_COVERAGE_BOUNDS);
const CORE_BOUNDS = latLngBounds(MAP_CORE_BOUNDS);

export default function Map({ industrieObjects, targetPosition, zoom }) {
  const { slug } = useParams();
  const slugExists = !!slug;
  const navigate = useNavigate();

  const { i18n } = useTranslation();
  const lang = i18n.language;

  const [defaultSeo, setDefaultSeo] = useState(null);

  const validIndustrieObjects = useMemo(
    () => industrieObjects.filter(isValidIndustrieObject),
    [industrieObjects]
  );
  const [saarlandGeoJson, setSaarlandGeoJson] = useState(null);

  // A single shared popup is rendered for the selected company instead of one
  // bound popup per marker. This keeps the marker layer cheap on first load.
  const [selectedObject, setSelectedObject] = useState(null);

  const handleSelect = useCallback((obj) => {
    setSelectedObject(obj);
    if (obj?.SeoCanonicalURL) navigate(`/${obj.SeoCanonicalURL}`);
  }, [navigate]);

  const handlePopupClose = useCallback(() => {
    setSelectedObject((prev) => {
      if (prev?.SeoCanonicalURL) navigate('/');
      return null;
    });
  }, [navigate]);

  // Keep the selection in sync with a deep-link slug (e.g. /company-name).
  useEffect(() => {
    if (!slugExists) return;
    const obj = validIndustrieObjects.find((o) => o.SeoCanonicalURL === slug);
    if (obj) setSelectedObject(obj);
  }, [slug, slugExists, validIndustrieObjects]);

  useEffect(() => {
    fetch('/data/Saarland.geojson')
      .then((res) => res.json())
      .then(setSaarlandGeoJson)
      .catch((err) => console.error('Failed to load Saarland GeoJSON:', err));
  }, []);

  useEffect(() => {
    strapiService.getSeo(lang)
      .then((res) => setDefaultSeo(res.data))
      .catch(() => {});
  }, [lang]);

  const seoTitle = selectedObject?.SeoMetaTitle || defaultSeo?.MetaTitle || "";
  const seoDescription = selectedObject?.SeoMetaDescription || defaultSeo?.MetaDescription || "";
  const seoCanonical = selectedObject?.SeoCanonicalURL
    ? `${DOMAIN_URL}/${selectedObject.SeoCanonicalURL}`
    : `${DOMAIN_URL}`;

  return (
    <>
      <SeoManager
        title={seoTitle}
        description={seoDescription}
        canonical={seoCanonical}
        lang={lang}
      />

      <MapContainer
        style={{ height: '100%' }}
        center={[49.38, 6.96]}
        zoom={9}
        // Nur ein Startwert - FitToTileCoverage setzt den echten minZoom aus der
        // Kachelabdeckung und der Fenstergroesse.
        minZoom={8}
        // Bewegungsbereich = Kachelabdeckung. viscosity=1 macht die Grenze hart,
        // damit man nicht ueber den Rand hinausschieben kann.
        maxBounds={COVERAGE_BOUNDS}
        maxBoundsViscosity={1.0}
        attributionControl={false}
        markerZoomAnimation={false}
      >
        <AttributionControl position="bottomright" prefix="Leaflet" />
        <FitToTileCoverage bounds={COVERAGE_BOUNDS} coreBounds={CORE_BOUNDS} />
        {saarlandGeoJson && <SaarlandOverlay geoJson={saarlandGeoJson} />}
        <MapTileLayers />
        <MarkerClusterGroup
          chunkedLoading
          iconCreateFunction={createClusterCustomIcon}
        >
          {validIndustrieObjects.map((obj) => (
            <CompanyMarker key={obj.id} industrieObject={obj} onSelect={handleSelect} />
          ))}
        </MarkerClusterGroup>
        {selectedObject && (
          <Popup
            className="custom-popup"
            position={[selectedObject.Coordinates.Latitude, selectedObject.Coordinates.Longitude]}
            eventHandlers={{ remove: handlePopupClose }}
          >
            <PopupContent industrieObject={selectedObject} />
          </Popup>
        )}
        {targetPosition && <FlyToMarker position={targetPosition} zoom={zoom} />}
      </MapContainer>
    </>
  );
}

// custom cluster icon
const createClusterCustomIcon = function (cluster) {
  return new divIcon({
    html: `<span class="cluster-icon">${cluster.getChildCount()}</span>`,
    className: "custom-marker-cluster",
    iconSize: point(33, 33, true)
  });
};

function isValidIndustrieObject(industrieObject) {
  return (industrieObject.Company && industrieObject.Coordinates.Latitude && industrieObject.Coordinates.Longitude);
}
